/*
 * Copyright Camunda Services GmbH and/or licensed to Camunda Services GmbH under
 * one or more contributor license agreements. See the NOTICE file distributed
 * with this work for additional information regarding copyright ownership.
 * Licensed under the Camunda License 1.0. You may not use this file
 * except in compliance with the Camunda License 1.0.
 */

import { DataTable, Table, TableHead, TableRow, TableHeader, TableCell, ProgressBar, TableBody } from '@carbon/react';

import { ExtractionStatusIndicator } from 'App/Pages/Project/IdpApplication/idp-project/idp-extraction-project/structured-extraction/test-template/ExtractionStatusIndicator';
import { EXTRACTED_RESULTS_NO_DATA_VALUE } from 'App/Pages/Project/IdpApplication/idp-project/utils/constants';

import * as Styled from './ExtractionResultsComparison.styled';

/**
 * @typedef {import('../../extracted-results/types').Field} Field
 * @typedef {import('../../extracted-results/types').Table} Table
 */

/**
 * @param {object} props
 * @param {object} props.extractedData - Contains fields and tables arrays from actual extraction
 * @param {object} props.templateData - Contains fields and tables arrays from template
 * @param {'fields' | 'tables'} props.type - Determines which data to display
 * @returns {JSX.Element}
 */
export const ExtractionResultsComparison = ({ extractedData, templateData, type }) => {
  const isFieldsView = type === 'fields';

  const templateItems = isFieldsView
    ? (templateData?.fields || []).filter((field) => field.active)
    : (templateData?.tables || []).filter((table) => table.active);

  const extractedItems = isFieldsView ? extractedData?.fields || [] : extractedData?.tables || [];

  const extractedItemsMap = new Map(extractedItems.map((item) => [item.id, item]));

  const comparisonRows = templateItems.map((templateItem) => {
    const extractedItem = extractedItemsMap.get(templateItem.id);
    const isExtracted = Boolean(extractedItem);

    const baseRow = {
      id: templateItem.id,
      variableName: templateItem.replacedName || templateItem.name,
      isExtracted,
      confidence: isExtracted ? parseInt(extractedItem.confidence, 10) : 0
    };

    if (isFieldsView) {
      return {
        ...baseRow,
        key: templateItem.name,
        value: isExtracted ? extractedItem.value : EXTRACTED_RESULTS_NO_DATA_VALUE
      };
    } else {
      return {
        ...baseRow,
        tableName: templateItem.name
      };
    }
  });

  const headers = isFieldsView
    ? [
        { key: 'variableName', header: 'Variable name' },
        { key: 'key', header: 'Key' },
        { key: 'value', header: 'Value' },
        { key: 'confidence', header: 'Average confidence' },
        { key: 'status', header: 'Status' }
      ]
    : [
        { key: 'variableName', header: 'Variable name' },
        { key: 'tableName', header: 'Table name' },
        { key: 'confidence', header: 'Average confidence' },
        { key: 'status', header: 'Status' }
      ];

  const renderStatusCell = (isExtracted) => <ExtractionStatusIndicator status={isExtracted} />;

  return (
    <DataTable rows={comparisonRows} headers={headers}>
      {({ rows, headers, getTableProps, getHeaderProps, getRowProps }) => (
        <Table {...getTableProps()}>
          <TableHead>
            <TableRow>
              {headers.map((header) => (
                <TableHeader {...getHeaderProps({ header })} key={header.key}>
                  {header.header}
                </TableHeader>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {rows.map((row) => {
              const rowData = comparisonRows.find((data) => data.id === row.id);
              return (
                <Styled.TableRow {...getRowProps({ row })} key={row.id}>
                  <TableCell>{rowData.variableName}</TableCell>
                  {isFieldsView ? (
                    <>
                      <TableCell>{rowData.key}</TableCell>
                      <TableCell>{rowData.value}</TableCell>
                    </>
                  ) : (
                    <TableCell>{rowData.tableName}</TableCell>
                  )}
                  <TableCell>
                    {rowData.isExtracted ? (
                      <ProgressBar value={rowData.confidence} max={100} size="small" label={`${rowData.confidence}%`} />
                    ) : (
                      EXTRACTED_RESULTS_NO_DATA_VALUE
                    )}
                  </TableCell>
                  <TableCell>{renderStatusCell(rowData.isExtracted)}</TableCell>
                </Styled.TableRow>
              );
            })}
          </TableBody>
        </Table>
      )}
    </DataTable>
  );
};
